import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  BarChart3,
  CalendarClock,
  Coffee,
  CreditCard,
  FileClock,
  Flag,
  Gauge,
  FileText,
  Gift,
  Image,
  Languages,
  LayoutGrid,
  LogOut,
  Menu as MenuIcon,
  MessageCircle,
  Percent,
  X,
  Tag,
  ReceiptText,
  RotateCcw,
  Search,
  Settings,
  ShieldCheck,
  Store,
  Truck,
  UtensilsCrossed,
  Users,
  WalletCards,
  type LucideIcon,
} from "lucide-react";
import { useAuthStore } from "../store/auth";
import { usePermissions } from "../hooks/usePermissions";
import { NotificationBell } from "../components/NotificationBell";
import { useAdminSettings } from "../api/hooks";
const SETTINGS_PATH = "/settings";

const SIDEBAR_W = 256;
let sidebarNavScrollTop = 0;

const SHELL_ICON_CLASS = "size-[18px] shrink-0";
// Below 900px the sidebar becomes an off-canvas drawer (translated fully out
// of view) instead of a permanently-visible icon rail — toggled via a
// hamburger button in the header. It only slides in as a full-width overlay
// when explicitly opened, so it never eats into the content viewport.
const SIDEBAR_CLASS = "fixed left-0 top-0 z-[100] flex h-screen w-[256px] flex-col border-r-0 bg-white shadow-none transition-transform duration-200 max-[900px]:!w-[280px] max-[900px]:-translate-x-full min-[901px]:max-[1280px]:!w-[220px]";
const SIDEBAR_OPEN_CLASS = "max-[900px]:!translate-x-0 max-[900px]:shadow-[0_0_40px_rgba(0,0,0,0.25)]";
const NAV_ITEM_BASE = "mb-1 flex min-h-10 w-full cursor-pointer items-center gap-2.5 rounded border-none px-4 py-3 text-left text-sm font-extrabold transition min-[901px]:max-[1280px]:gap-2 min-[901px]:max-[1280px]:px-3 min-[901px]:max-[1280px]:py-2.5 min-[901px]:max-[1280px]:text-[13px]";
const NAV_ITEM_IDLE = "!bg-transparent text-[#525252] hover:!bg-[#fff7ed] hover:text-[#c2410c]";
const NAV_ITEM_ACTIVE = "!bg-[#fff7ed] !text-[#c2410c] shadow-none hover:!bg-[#fff7ed] hover:!text-[#c2410c]";
const MAIN_WRAP_CLASS = "min-h-screen max-[900px]:!ml-0 min-[901px]:max-[1280px]:!ml-[220px]";
const HEADER_CLASS = "fixed right-0 top-0 z-[99] flex h-16 items-center gap-4 border-b-0 bg-[#f8f9fa] px-8 shadow-none backdrop-blur-[6px] max-[900px]:!left-0 max-[900px]:px-4 min-[901px]:max-[1280px]:!left-[220px] min-[901px]:max-[1280px]:px-5";
const CONTENT_CLASS = "min-h-[calc(100vh-64px)] bg-[#f8f9fa] pt-16 max-[900px]:px-4 max-[900px]:pb-4 max-[900px]:pt-[88px] max-[1280px]:overflow-x-hidden";
const BACKDROP_CLASS = "fixed inset-0 z-[98] hidden bg-black/40 max-[900px]:block";

const ICONS = {
  dashboard: Gauge,
  orders: ReceiptText,
  transactions: CreditCard,
  refunds: RotateCcw,
  menu: UtensilsCrossed,
  categories: LayoutGrid,
  vouchers: Percent,
  bundles: Gift,
  stalls: Store,
  wallet: WalletCards,
  delivery: Truck,
  sms: MessageCircle,
  image: Image,
  staff: Users,
  admins: ShieldCheck,
  reports: BarChart3,
  activity: FileClock,
  flags: Flag,
  pricing: Tag,
  hours: CalendarClock,
  legal: FileText,
  settings: Settings,
  search: Search,
  globe: Languages,
  logout: LogOut,
  brand: Coffee,
} satisfies Record<string, LucideIcon>;

type IconKey = keyof typeof ICONS;

function Icon({ name, className = "" }: { name: IconKey; className?: string }) {
  const LucideIconComponent = ICONS[name];
  return <LucideIconComponent className={`${SHELL_ICON_CLASS} ${className}`} strokeWidth={2.4} aria-hidden="true" />;
}

function initials(name: string | undefined) {
  return (name ?? "A").trim().slice(0, 1).toUpperCase();
}

export function AdminShell({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const staff = useAuthStore((s) => s.staff);
  const logout = useAuthStore((s) => s.logout);
  const { t, i18n } = useTranslation();
  const { canRoute } = usePermissions();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const sidebarNavRef = useRef<HTMLElement>(null);

  const allNavItems: { key: string; icon: IconKey; label: string }[] = [
    { key: "/", icon: "dashboard", label: t("nav.dashboard") },
    { key: "/orders", icon: "orders", label: t("nav.orders") },
    { key: "/transactions", icon: "transactions", label: t("nav.transactions") },
    { key: "/refunds", icon: "refunds", label: t("nav.refunds") },
    { key: "/menu", icon: "menu", label: t("nav.menu") },
    { key: "/categories", icon: "categories", label: t("nav.categories") },
    { key: "/vouchers", icon: "vouchers", label: t("nav.vouchers") },
    { key: "/bundles", icon: "bundles", label: t("nav.bundles") },
    { key: "/stalls", icon: "stalls", label: t("nav.stalls") },
    { key: "/stall-applications", icon: "stalls", label: t("nav.stallApplications") },
    { key: "/payment-methods", icon: "wallet", label: t("nav.paymentMethods") },
    { key: "/delivery-providers", icon: "delivery", label: t("nav.deliveryProviders") },
    { key: "/sms-providers", icon: "sms", label: t("nav.smsProviders") },
    { key: "/banners", icon: "image", label: t("nav.banners") },
    { key: "/admins", icon: "admins", label: t("nav.adminAccounts") },
    { key: "/staff", icon: "staff", label: t("nav.staff") },
    { key: "/customers", icon: "staff", label: t("nav.customers") },
    { key: "/reports", icon: "reports", label: t("nav.reports") },
    { key: "/activity", icon: "activity", label: t("nav.activity") },
    { key: "/flags", icon: "flags", label: t("nav.flags") },
    { key: "/pricing-rules", icon: "pricing", label: t("nav.pricingRules") },
    { key: "/hours", icon: "hours", label: t("nav.hours") },
    { key: "/legal-content", icon: "legal", label: t("nav.legalContent") },
  ];

  const navItems = allNavItems.filter((item) => canRoute(item.key));
  const canSettings = canRoute("/settings");
  // Delivery is on but the shop has no coordinates: distances are stored as 0, the delivery radius is not enforced and Lalamove quotes from a
  // placeholder point. Said loudly on every page until it is fixed (only for roles that can open Settings).
  const { data: shopSettings } = useAdminSettings({ enabled: canSettings });
  const shopCoordsMissing = !!shopSettings?.data && shopSettings.data.delivery_control_enabled !== false && (shopSettings.data.lat == null || shopSettings.data.lng == null);

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setUserMenuOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, []);

  useEffect(() => {
    if (!mobileNavOpen) return;

    const previousBodyOverflow = document.body.style.overflow;
    const previousRootOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousRootOverflow;
    };
  }, [mobileNavOpen]);

  useLayoutEffect(() => {
    const nav = sidebarNavRef.current;
    if (!nav) return;

    nav.scrollTop = sidebarNavScrollTop;
    const frame = window.requestAnimationFrame(() => {
      nav.scrollTop = sidebarNavScrollTop;
    });
    const rememberScroll = () => {
      sidebarNavScrollTop = nav.scrollTop;
    };

    nav.addEventListener("scroll", rememberScroll, { passive: true });
    return () => {
      window.cancelAnimationFrame(frame);
      sidebarNavScrollTop = nav.scrollTop;
      nav.removeEventListener("scroll", rememberScroll);
    };
  }, []);

  const goToNavItem = (path: string) => {
    if (sidebarNavRef.current) {
      sidebarNavScrollTop = sidebarNavRef.current.scrollTop;
    }
    setMobileNavOpen(false);
    void navigate(path);
  };

  const refundSource = new URLSearchParams(location.search).get("from");
  const activeNavKey = (location.state as { activeNav?: string } | null)?.activeNav
    ?? (refundSource === "orders" ? "/orders" : refundSource === "transactions" ? "/transactions" : refundSource === "refunds" ? "/refunds" : undefined);
  const isActive = (path: string) => {
    if (activeNavKey) return path === activeNavKey;
    if (path === "/") return location.pathname === "/";
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const LANGUAGES: { code: "en" | "zh" | "ms"; label: string }[] = [
    { code: "en", label: "English" },
    { code: "zh", label: "中文" },
    { code: "ms", label: "Bahasa Melayu" },
  ];

  const setLang = (code: string) => {
    void i18n.changeLanguage(code);
    localStorage.setItem("kawkaw_admin_lang", code);
    setUserMenuOpen(false);
  };

  const signOut = () => {
    logout();
    setUserMenuOpen(false);
    void navigate("/login");
  };

  return (
    <div className="admin-shell min-h-screen bg-[#f8f9fa] text-[#191c1d]">
      {mobileNavOpen && <div className={BACKDROP_CLASS} onClick={() => setMobileNavOpen(false)} />}
      <aside className={`${SIDEBAR_CLASS} ${mobileNavOpen ? SIDEBAR_OPEN_CLASS : ""}`} style={{ width: SIDEBAR_W }}>
        <div className="flex h-16 shrink-0 items-center gap-3 border-b-0 px-6 min-[901px]:max-[1280px]:px-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-[#ff8c00] text-white shadow-none [&_svg]:size-5">
            <Icon name="brand" className="size-5" />
          </div>
          <div className="min-w-0 flex-1 leading-[1.2] max-[900px]:block">
            <div className="overflow-hidden text-ellipsis whitespace-nowrap text-[15px] font-black text-[#ea580c] min-[901px]:max-[1280px]:text-sm">{t("nav.brandTitle")}</div>
            <div className="mt-[3px] overflow-hidden text-ellipsis whitespace-nowrap text-[11px] font-semibold text-[#737373] min-[901px]:max-[1280px]:text-[10px]">{t("nav.brandSubtitle")}</div>
          </div>
          <button
            type="button"
            onClick={() => setMobileNavOpen(false)}
            aria-label={t("common.close", "Close")}
            className="hidden shrink-0 cursor-pointer items-center justify-center rounded-lg border-none bg-transparent p-1.5 text-[#737373] hover:bg-[#f5f5f5] max-[900px]:flex"
          >
            <X className="size-5" />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          <nav ref={sidebarNavRef} className="min-h-0 flex-1 overflow-y-auto px-2.5 pb-2.5 min-[901px]:max-[1280px]:px-2">
            {navItems.map((item) => {
              const active = isActive(item.key);
              return (
                <button
	                  key={item.key}
	                  type="button"
	                  onMouseDown={(event) => event.preventDefault()}
	                  onClick={() => goToNavItem(item.key)}
	                  className={`${NAV_ITEM_BASE} ${active ? NAV_ITEM_ACTIVE : NAV_ITEM_IDLE}`}
	                >
                  <Icon name={item.icon} />
                  <span className="admin-nav-label min-w-0">{item.label}</span>
                </button>
              );
            })}
          </nav>
          {canSettings && (
            <div className="border-t border-[#f5f5f5] p-2.5 [&_button]:mb-0">
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => goToNavItem("/settings")}
                className={`${NAV_ITEM_BASE} mb-0 ${isActive("/settings") ? NAV_ITEM_ACTIVE : NAV_ITEM_IDLE}`}
              >
                <Icon name="settings" />
                <span className="admin-nav-label min-w-0">{t("nav.settings")}</span>
              </button>
            </div>
          )}
        </div>
      </aside>

      <div className={MAIN_WRAP_CLASS} style={{ marginLeft: SIDEBAR_W }}>
        <header
          className={HEADER_CLASS}
          style={{ left: SIDEBAR_W }}
        >
          <div className="admin-header-inner">
            <button
              type="button"
              onClick={() => setMobileNavOpen(true)}
              aria-label={t("common.openMenu", "Open navigation")}
              className="hidden shrink-0 cursor-pointer items-center justify-center rounded-lg border-none bg-transparent p-2 text-[#191c1d] hover:bg-[#f1f5f9] max-[900px]:flex"
            >
              <MenuIcon className="size-5" />
            </button>
            <div className="relative max-w-96 flex-1 rounded-xl bg-[#f3f4f5] max-[640px]:max-w-none max-[1024px]:max-w-[300px]">
              <Icon name="search" className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#737373]" />
              <input
                type="search"
                placeholder={t("common.search")}
                onKeyDown={(e) => {
                  // Enter searches orders (code / customer / phone) — the one global lookup staff actually need.
                  const q = e.currentTarget.value.trim();
                  if (e.key === "Enter" && q) navigate(`/orders?q=${encodeURIComponent(q)}`);
                }}
                className="h-9 w-full rounded-xl border border-transparent bg-[#f3f4f5] py-0 pl-12 pr-4 text-sm font-bold text-[#191c1d] outline-none transition placeholder:text-[#737373] focus:border-[rgba(249,115,22,0.35)] focus:bg-white focus:shadow-[0_0_0_4px_rgba(255,237,213,0.9)]"
              />
            </div>

            <div className="admin-header-actions ml-auto flex items-center gap-6 max-[1024px]:gap-3.5">
              <NotificationBell />

              <div className="h-8 w-px shrink-0 bg-[#e2e8f0]" />

              <div ref={menuRef} className="relative">
                <button
                  type="button"
                  onClick={() => setUserMenuOpen((open) => !open)}
                  className="flex min-h-11 cursor-pointer items-center gap-3 rounded-[18px] border-none bg-transparent py-[5px] pl-3 pr-2 hover:bg-[#f8fafc]"
                >
                  <span className="block text-right leading-[1.2] max-[900px]:hidden">
                    <span className="block max-w-40 overflow-hidden text-ellipsis whitespace-nowrap text-[13px] font-black text-[#111827]">{staff?.name ?? t("staff.admin")}</span>
                    <span className="mt-[3px] block text-[10px] font-black uppercase text-[#d97706]">{staff?.role ?? ""}</span>
                  </span>
                  <span className="flex size-9 items-center justify-center rounded-full bg-[#ff8c00] text-sm font-black text-white shadow-[0_8px_18px_rgba(249,115,22,0.22)]">
                    {initials(staff?.name)}
                  </span>
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 top-[calc(100%+8px)] w-[220px] overflow-hidden rounded-[18px] border border-[#e5e7eb] bg-white p-2 shadow-[0_16px_40px_rgba(15,23,42,0.14)]">
                    <div className="px-3 pb-1 pt-2 text-[10px] font-black uppercase tracking-wide text-[#a3a3a3]">{t("common.language")}</div>
                    {LANGUAGES.map((lng) => (
                      <button
                        key={lng.code}
                        type="button"
                        onClick={() => setLang(lng.code)}
                        className={`flex w-full cursor-pointer items-center gap-2.5 rounded-xl border-none px-3 py-2.5 text-left text-sm font-extrabold ${
                          i18n.language === lng.code ? "bg-[#fff7ed] text-[#ea580c]" : "bg-transparent text-[#334155] hover:bg-[#fff7ed] hover:text-[#ea580c]"
                        }`}
                      >
                        <Icon name="globe" />
                        {lng.label}
                      </button>
                    ))}
                    <div className="my-1 h-px bg-[#f1f5f9]" />
                    <button
                      type="button"
                      onClick={signOut}
                      className="flex w-full cursor-pointer items-center gap-2.5 rounded-xl border-none bg-transparent px-3 py-2.5 text-left text-sm font-extrabold text-[#dc2626] hover:bg-[#fef2f2] hover:text-[#b91c1c]"
                    >
                      <Icon name="logout" />
                      {t("common.signOut")}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <main id="admin-content" className={CONTENT_CLASS} data-admin-view={location.pathname}>
          {shopCoordsMissing && location.pathname !== `${SETTINGS_PATH}` && (
            <div role="alert" data-testid="coords-missing-banner" className="admin-coords-banner" style={{ margin: "16px 16px 0", padding: "12px 16px", borderRadius: 12, background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B", fontSize: 14, lineHeight: 1.5, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ flex: "1 1 260px" }}>{t("settingsScreen.coordsMissingBanner")}</span>
              <button type="button" onClick={() => navigate(SETTINGS_PATH)} style={{ border: "none", borderRadius: 8, padding: "8px 14px", background: "#DC2626", color: "#fff", fontWeight: 700, cursor: "pointer" }}>{t("settingsScreen.coordsMissingAction")}</button>
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
}
